import React, { useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Search as SearchIcon,
  X,
  Sidebar,
} from "react-feather";
import type { PdfToolbarContext } from "./types";

interface PdfToolbarProps {
  ctx: PdfToolbarContext;
  /**
   * Extra controls appended as a trailing toolbar group, for a `toolbar`
   * render function that wants the defaults plus its own buttons.
   */
  children?: React.ReactNode;
}

/**
 * Default toolbar. Purely a consumer of {@link PdfToolbarContext} — a custom
 * toolbar passed via the `toolbar` prop receives the same context and can
 * replicate or extend any of this.
 */
export function PdfToolbar({ ctx, children }: PdfToolbarProps) {
  const { features } = ctx;
  const [pageInput, setPageInput] = useState(String(ctx.currentPage));
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    setPageInput(String(ctx.currentPage));
  }, [ctx.currentPage]);

  const commitPage = () => {
    const n = parseInt(pageInput, 10);
    if (!isNaN(n)) {
      ctx.goToPage(n);
    } else {
      setPageInput(String(ctx.currentPage));
    }
  };

  const runSearch = (value: string) => {
    setQuery(value);
    ctx.search(value);
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setQuery("");
    ctx.clearSearch();
  };

  const hasMatches = ctx.searchResults.total > 0;

  return (
    <div className="pdf-renderer__toolbar">
      {features.thumbnails && (
        <div className="pdf-renderer__toolbar-group">
          <button
            type="button"
            className="pdf-renderer__tool-btn"
            aria-label="Toggle thumbnails"
            aria-pressed={ctx.thumbnailsOpen}
            onClick={ctx.toggleThumbnails}
          >
            <Sidebar size={16} />
          </button>
        </div>
      )}

      {features.pageNavigation && (
        <div className="pdf-renderer__toolbar-group">
          <button
            type="button"
            className="pdf-renderer__tool-btn"
            aria-label="Previous page"
            disabled={ctx.currentPage <= 1}
            onClick={ctx.previousPage}
          >
            <ChevronLeft size={16} />
          </button>
          <div className="pdf-renderer__page-indicator">
            <input
              className="pdf-renderer__page-input"
              value={pageInput}
              aria-label="Page number"
              inputMode="numeric"
              onChange={(e) =>
                setPageInput(e.target.value.replace(/[^0-9]/g, ""))
              }
              onBlur={commitPage}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitPage();
              }}
            />
            <span className="pdf-renderer__page-total">
              / {ctx.totalPages || "–"}
            </span>
          </div>
          <button
            type="button"
            className="pdf-renderer__tool-btn"
            aria-label="Next page"
            disabled={ctx.totalPages > 0 && ctx.currentPage >= ctx.totalPages}
            onClick={ctx.nextPage}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {features.zoom && (
        <div className="pdf-renderer__toolbar-group">
          <button
            type="button"
            className="pdf-renderer__tool-btn"
            aria-label="Zoom out"
            onClick={ctx.zoomOut}
          >
            <ZoomOut size={16} />
          </button>
          <span className="pdf-renderer__zoom-value">
            {Math.round(ctx.zoom * 100)}%
          </span>
          <button
            type="button"
            className="pdf-renderer__tool-btn"
            aria-label="Zoom in"
            onClick={ctx.zoomIn}
          >
            <ZoomIn size={16} />
          </button>
        </div>
      )}

      {features.search && (
        <div className="pdf-renderer__toolbar-group pdf-renderer__toolbar-group--search">
          {searchOpen ? (
            <div className="pdf-renderer__search">
              <SearchIcon size={14} className="pdf-renderer__search-icon" />
              <input
                autoFocus
                className="pdf-renderer__search-input"
                placeholder="Search"
                value={query}
                onChange={(e) => runSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    if (e.shiftKey) ctx.previousSearchResult();
                    else ctx.nextSearchResult();
                  }
                  if (e.key === "Escape") closeSearch();
                }}
              />
              <span className="pdf-renderer__search-count">
                {hasMatches
                  ? `${ctx.searchResults.activeResultIndex + 1}/${ctx.searchResults.total}`
                  : query
                    ? "0/0"
                    : ""}
              </span>
              <button
                type="button"
                className="pdf-renderer__tool-btn"
                aria-label="Previous match"
                disabled={!hasMatches}
                onClick={ctx.previousSearchResult}
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                className="pdf-renderer__tool-btn"
                aria-label="Next match"
                disabled={!hasMatches}
                onClick={ctx.nextSearchResult}
              >
                <ChevronRight size={16} />
              </button>
              <button
                type="button"
                className="pdf-renderer__tool-btn"
                aria-label="Close search"
                onClick={closeSearch}
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="pdf-renderer__tool-btn"
              aria-label="Search"
              onClick={() => setSearchOpen(true)}
            >
              <SearchIcon size={16} />
            </button>
          )}
        </div>
      )}

      {children != null && (
        <div className="pdf-renderer__toolbar-group">{children}</div>
      )}
    </div>
  );
}

export default PdfToolbar;
